import { classes } from 'html-classes';
import type { FC, ReactNode } from 'react';

import Icon from '~/components/Icon/Icon';

import styles from './InlineNotification.module.scss';

export type InlineNotificationVariant = 'success' | 'error' | 'warning';

interface InlineNotificationProps {
  /** Тип уведомления */
  variant?: InlineNotificationVariant;
  /** Заголовок уведомления */
  title: string;
  /** Текст описания */
  description?: string;
  /** Дополнительные элементы (например, кнопки) */
  children?: ReactNode;
  /** Дополнительный className */
  className?: string;
  classes?: {
    root?: string;
    title?: string;
    description?: string;
  };
  onClose?: () => void;
}

const getVariantClass = (variant: InlineNotificationVariant) => {
  switch (variant) {
    case 'error':
      return styles._error;
    case 'warning':
      return styles._warning;
    default:
      return styles._success;
  }
};

/**
 * Компонент inline-уведомления.
 * Может отображать состояния «успех» или «ошибка».
 */
export const InlineNotification: FC<InlineNotificationProps> = ({
  variant = 'success',
  title,
  description,
  children,
  className,
  classes: classNames,
  onClose,
}) => {
  const {
    root: rootClassName = className,
    title: titleClassName,
    description: descriptionClassName,
  } = classNames || {};

  return (
    <div
      role="alert"
      className={classes([
        styles.inlineNotification,
        getVariantClass(variant),
        rootClassName,
      ])}
    >
      <div className={styles.header}>
        <p className={classes([styles.title, titleClassName])}>{title}</p>
        {onClose && (
          <span
            className={styles.close}
            role="button"
            onClick={onClose}
            tabIndex={-1}
          >
            <Icon type="close" size={20} />
          </span>
        )}
      </div>
      {(description || children) && (
        <div className={classes([styles.description, descriptionClassName])}>
          <p className={styles.text}>{description}</p>
          {children && <div className={styles.actions}>{children}</div>}
        </div>
      )}
    </div>
  );
};

export default InlineNotification;
